import { z } from 'zod';
import { URLSearchParams } from 'url';
import { SubscriberEmailSchema, normalizeSubscriberEmail } from './email-lists.js';

// ─────────────────────────────────────────────────────────────────
// Unsubscribing
//
// Every email footer carries an opt-out link. It is built here so that the
// three things that touch it cannot disagree about its shape:
//
//   · the builder, which puts it in the footer of a rendered email
//   · the sender job, which fills in whose copy it is
//   · the public page, which reads it back out of the query string
//
//   {BASE_URL}/unsubscribe?workspace=<uuid>&email=<email id>&recipient=<address>
//
// `workspace` and `email` are context — which sender, and which campaign the
// click came from — and both are required: the page refuses a link that cannot
// say which send it is, rather than guessing at what to unsubscribe. The email
// is what the opt-out is derived from (its audience's lists, or the whole
// workspace for a send with no lasting list behind it).
//
// `recipient` is the one person the link is for, and it is the only address
// anything will ever act on. It is the only per-recipient part, so a build that
// does not yet know who will receive it carries
// UNSUBSCRIBE_RECIPIENT_PLACEHOLDER in its place and the sender fills the real
// address in per delivery (fillUnsubscribeRecipient). That is what lets one
// rendered body serve an entire audience: without it, every copy of every
// email would need its own render purely to spell out a different link, which
// is exactly the work the sender's render cache exists to avoid. A link that
// still carries the placeholder unsubscribes nobody — it was never sent to
// anyone — so the page refuses it too.
//
// The link is not signed. It has to keep working for as long as it sits in
// someone's inbox, and a signature over it would silently break every
// already-delivered link the day the serving secret is rotated — a rotation
// nobody would connect to unsubscribe. So the page trusts the address the link
// names, which means a forged one can unsubscribe an address its holder could
// unsubscribe by hand anyway. What it does not do is invite it: the page
// offers no field in which to name somebody else.
// ─────────────────────────────────────────────────────────────────

/** Public path the unsubscribe page is served at. */
export const UNSUBSCRIBE_PATH = '/unsubscribe';

/**
 * Where a mail client posts a one-click opt-out (RFC 8058).
 *
 * The page and the machine target are deliberately different paths. They have
 * nothing in common but the vocabulary: the page is for a person and has to
 * render, ask, and answer; the one-click target is for Gmail's servers, which
 * POST a fixed body and read nothing but the status code. Keeping them apart
 * lets each be limited, logged and answered in the way its caller deserves —
 * and the machine path never has to grow a form or a rate limit tuned for
 * humans.
 */
export const UNSUBSCRIBE_ONE_CLICK_PATH = '/unsubscribe/one-click';

/**
 * The form field and value RFC 8058 specifies for that POST. A one-click
 * request is `List-Unsubscribe=One-Click` and nothing else — no session, no
 * scope, no confirmation.
 */
export const UNSUBSCRIBE_ONE_CLICK_FIELD = 'List-Unsubscribe';
export const UNSUBSCRIBE_ONE_CLICK_VALUE = 'One-Click';

/**
 * Stands in for the recipient's address in a rendered body.
 *
 * Only characters the `application/x-www-form-urlencoded` serialiser leaves
 * alone (alphanumerics, `-`, `_`, `.`, `*`) — a placeholder that got
 * percent-encoded when the URL was built could not be found again by the
 * sender, and the link would go out broken.
 */
export const UNSUBSCRIBE_RECIPIENT_PLACEHOLDER = '__BF_RECIPIENT__';

/** Feedback is a courtesy, not a form: long enough to say something, not to paste a novel. */
export const UNSUBSCRIBE_FEEDBACK_MAX = 1000;

/** What an opt-out can cover: one list, or the sender's whole output. */
export const UNSUBSCRIBE_SCOPES = ['list', 'workspace'] as const;
export const UnsubscribeScopeSchema = z.enum(UNSUBSCRIBE_SCOPES);
export type UnsubscribeScope = z.infer<typeof UnsubscribeScopeSchema>;

// ── Why they left ─────────────────────────────────────────────────

/**
 * The reasons the page offers. Coded rather than free text because they are
 * meant to be counted later; the free textarea beside them is where the
 * detail goes, and neither is required — an opt-out must never be behind a
 * form the visitor has to fill in.
 */
export const UNSUBSCRIBE_REASONS = [
  { value: 'too_frequent', label: 'Too many emails' },
  { value: 'not_relevant', label: 'The content is not relevant to me' },
  { value: 'never_subscribed', label: 'I never subscribed to this list' },
  { value: 'signed_up_by_mistake', label: 'I signed up by mistake' },
  { value: 'other', label: 'Something else' },
] as const;

export const UNSUBSCRIBE_REASON_VALUES = UNSUBSCRIBE_REASONS.map((reason) => reason.value) as [
  (typeof UNSUBSCRIBE_REASONS)[number]['value'],
  ...(typeof UNSUBSCRIBE_REASONS)[number]['value'][],
];

export const UnsubscribeReasonSchema = z.enum(UNSUBSCRIBE_REASON_VALUES);
export type UnsubscribeReason = z.infer<typeof UnsubscribeReasonSchema>;

/** Human label for a stored reason; the raw value when it is not a known one. */
export function unsubscribeReasonLabel(reason: string): string {
  return UNSUBSCRIBE_REASONS.find((entry) => entry.value === reason)?.label ?? reason;
}

// ── The link ──────────────────────────────────────────────────────

export interface UnsubscribeUrlInput {
  /** Deployment's public URL (process.env.BASE_URL). */
  baseUrl: string;
  workspaceId: string;
  /** The email the link is rendered into. */
  emailId: string;
  /**
   * Whose copy this is. Omitted (or blank) leaves the placeholder in the URL
   * for the sender to fill in — see fillUnsubscribeRecipient.
   */
  recipientEmail?: string | null | undefined;
}

/**
 * The opt-out URL for an email, with the recipient's address either spelled
 * out (a test send knows who it is going to) or left as the placeholder.
 */
export function buildUnsubscribeUrl(input: UnsubscribeUrlInput): string {
  return unsubscribeUrlFor(UNSUBSCRIBE_PATH, input);
}

/**
 * The URL a mail client POSTs to for one-click opt-out (RFC 8058).
 *
 * Never carries the placeholder: a client that posted it would be asking to
 * unsubscribe nobody in particular, and the sender always knows the address it
 * is delivering to — see unsubscribeHeaders, which is the only caller.
 */
export function buildOneClickUnsubscribeUrl(input: OneClickUnsubscribeInput): string {
  return unsubscribeUrlFor(UNSUBSCRIBE_ONE_CLICK_PATH, input);
}

/** The one-click URL's input: there is always an address to act on. */
export interface OneClickUnsubscribeInput extends UnsubscribeUrlInput {
  recipientEmail: string;
}

function unsubscribeUrlFor(path: string, input: UnsubscribeUrlInput): string {
  const params = new URLSearchParams({
    workspace: input.workspaceId,
    email: input.emailId,
    recipient: input.recipientEmail
      ? normalizeSubscriberEmail(input.recipientEmail)
      : UNSUBSCRIBE_RECIPIENT_PLACEHOLDER,
  });
  return `${input.baseUrl.replace(/\/+$/, '')}${path}?${params.toString()}`;
}

/**
 * The headers that let a mail client offer its own unsubscribe button
 * (RFC 8058 / RFC 2369), one recipient at a time.
 *
 * `List-Unsubscribe` is the RFC 2369 header every client reads to show a link;
 * `List-Unsubscribe-Post` is what tells the ones that support it (Gmail and
 * Yahoo for bulk senders) that they may skip the browser and POST instead.
 * Both are worth sending: without the second, the first is only a courtesy to
 * clients that choose to render it, and a recipient who cannot find the
 * footer's link has no way out. Without either, the mail is more likely to be
 * reported as spam than to be unsubscribed from.
 */
export function unsubscribeHeaders(input: OneClickUnsubscribeInput): Record<string, string> {
  return {
    'List-Unsubscribe': `<${buildOneClickUnsubscribeUrl(input)}>`,
    'List-Unsubscribe-Post': `${UNSUBSCRIBE_ONE_CLICK_FIELD}=${UNSUBSCRIBE_ONE_CLICK_VALUE}`,
  };
}

/** Whether a parsed form body is RFC 8058's one-click request. */
export function isOneClickUnsubscribeBody(body: Record<string, unknown>): boolean {
  return body[UNSUBSCRIBE_ONE_CLICK_FIELD] === UNSUBSCRIBE_ONE_CLICK_VALUE;
}

/**
 * Fill a rendered body's placeholder with the address it is being delivered
 * to. A body that already names a recipient (built for one) passes through
 * untouched, so the sender can call this unconditionally.
 */
export function fillUnsubscribeRecipient(html: string, recipientEmail: string): string {
  if (!html.includes(UNSUBSCRIBE_RECIPIENT_PLACEHOLDER)) return html;
  const address = encodeURIComponent(normalizeSubscriberEmail(recipientEmail));
  return html.split(UNSUBSCRIBE_RECIPIENT_PLACEHOLDER).join(address);
}

/**
 * Whether a `recipient` query value is really an address, or the placeholder
 * a build that had no recipient left behind. A previewed or hand-opened build
 * carries the placeholder, and the page treats that as "we do not know who
 * this is" rather than as an address nobody could have.
 */
export function isUnsubscribePlaceholder(value: string | null | undefined): boolean {
  if (!value) return true;
  const trimmed = value.trim();
  return (
    trimmed === '' ||
    trimmed === UNSUBSCRIBE_RECIPIENT_PLACEHOLDER ||
    decodeURIComponent(trimmed) === UNSUBSCRIBE_RECIPIENT_PLACEHOLDER
  );
}

// ── Arriving mangled ──────────────────────────────────────────────

/** The spellings an escaped ampersand arrives in: `&amp;`, `&#38;`, `&#x26;`. */
const ESCAPED_AMPERSAND = /&(?:amp|#0*38|#x0*26);/gi;

/**
 * Read a link back out of the form it arrives in, when it arrives HTML-escaped.
 *
 * The href we render is valid HTML: an `&` between query parameters belongs
 * there as `&amp;`, and a browser following the link decodes it before
 * requesting anything. What a *copy* of that URL keeps is the encoded text,
 * and a copy is how a link reaches the page more often than anyone would like
 * — a mail client's "copy link address", a link quoted inside a forwarded
 * reply, a plain-text rendering of an HTML mail, a reader retyping what they
 * see, or the link pasted into a document and opened from there.
 *
 * Such a request does not merely look odd: `?workspace=x&amp;email=y` splits
 * on the real ampersands into `workspace=x` and `amp;email=y`, so `email` and
 * `recipient` are *absent* rather than wrong. Refusing that would turn an
 * opt-out into a spam complaint over a correctly-encoded URL, so the encoded
 * spelling is read as the parameter it means. Both layers of the same mangling
 * are undone: the `amp;` a parameter name picks up from the split, and any
 * `&amp;` left inside a value.
 *
 * Unknown parameters pass through untouched, and a value that is not a string
 * (a nested object from a lenient query parser) is left exactly as it was.
 * When one name arrives both ways, the last spelling wins.
 */
export function repairEscapedQuery(input: Record<string, unknown>): Record<string, unknown> {
  const repaired: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(input)) {
    // `amp;email` → `email`; a link escaped twice over arrives `amp;amp;email`.
    let key = name;
    while (/^amp;/i.test(key)) key = key.slice('amp;'.length);
    repaired[key] = typeof value === 'string' ? value.replace(ESCAPED_AMPERSAND, '&') : value;
  }
  return repaired;
}

// ── Schemas ───────────────────────────────────────────────────────

/**
 * The query string the page is opened with.
 *
 * `email` is required — the id of the send the link was rendered into — because
 * it is what the opt-out is derived from: which list, or which whole workspace,
 * the visitor is leaving. A link that names no email names nothing anyone could
 * unsubscribe from, so the page refuses it rather than guessing on the
 * visitor's behalf.
 */
export const UnsubscribeQuerySchema = z.object({
  workspace: z.string().uuid('Invalid workspace'),
  email: z.string().trim().min(1, 'Missing email').max(64),
  recipient: z.string().trim().max(320).optional(),
});
export type UnsubscribeQuery = z.infer<typeof UnsubscribeQuerySchema>;

/**
 * A submitted opt-out.
 *
 * `workspace` and `email` ride along as hidden fields so the page can be
 * re-posted (a refresh, a back button) without the query string, and so the
 * server can re-resolve the campaign context it is recording against. Both are
 * required for the same reason the link needs them: without the email there is
 * no send to take the coverage from, and a submission that lost it is refused
 * rather than escalated into an opt-out of everything the workspace sends.
 *
 * What the opt-out *covers* is deliberately not a field. The page offers no
 * choice — the visitor is not asked whether they want to leave one list or
 * everything, because they never chose which of those the email reached them
 * through. The scope is derived from the email the link came from, server
 * side: its lists, or the whole workspace for a send whose audience was an
 * uploaded CSV (apps/server/src/email/unsubscribe-target.ts). Anything a form
 * could say about it would be a value a stranger could type.
 */
export const UnsubscribeRequestSchema = z.object({
  workspace: z.string().uuid('Invalid workspace'),
  email: z.string().trim().min(1, 'Missing email').max(64),
  recipient: SubscriberEmailSchema,
  reason: UnsubscribeReasonSchema.nullish(),
  feedback: z.string().trim().max(UNSUBSCRIBE_FEEDBACK_MAX).nullish(),
});
export type UnsubscribeRequest = z.infer<typeof UnsubscribeRequestSchema>;

/** One stored opt-out, as the workspace's own members read it back. */
export const UnsubscribeSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  scope: UnsubscribeScopeSchema,
  /** The list an opt-out covers; null for a workspace-wide one. */
  listId: z.string().uuid().nullable(),
  listName: z.string().nullable(),
  /** The email the visitor clicked through from, when the link carried one. */
  emailId: z.string().nullable(),
  emailSubject: z.string().nullable(),
  reason: z.string().nullable(),
  feedback: z.string().nullable(),
  createdAt: z.string(),
});
export type Unsubscribe = z.infer<typeof UnsubscribeSchema>;

export const UnsubscribeListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  /** Filter to one list's opt-outs; `workspace` selects the sender-wide ones. */
  listId: z.string().uuid().optional(),
  scope: UnsubscribeScopeSchema.optional(),
});
export type UnsubscribeListQuery = z.infer<typeof UnsubscribeListQuerySchema>;

export const PaginatedUnsubscribesSchema = z.object({
  data: z.array(UnsubscribeSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type PaginatedUnsubscribes = z.infer<typeof PaginatedUnsubscribesSchema>;
